import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaBuilding,
  FaCube,
  FaTag,
  FaRupeeSign,
  FaCommentAlt,
  FaPaperPlane,
  FaMapMarkerAlt,
  FaClock,
  FaWhatsapp,
} from "react-icons/fa";

import "./Contact.css";
import { BASE_URL } from "../../Config/config";

export default function Contact() {
  const [searchParams] = useSearchParams();

  /* =====================================================
     FORM DATA
  ===================================================== */

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    businessName: "",
    module: "",
    moduleCode: "",
    modulePrice: "",
    message: "",
  });

  /* =====================================================
     CONTACT DETAILS FROM BACKEND
  ===================================================== */

  const [contactData, setContactData] = useState({
    phone: "",
    email: "",
    address: "",
    businessHours: "",
  });

  const [contactLoading, setContactLoading] = useState(true);

  /* =====================================================
     MODULES
  ===================================================== */

  const modules = [
    {
      name: "Tally Prime",
      code: "TALLY-PRIME",
      price: "Contact for Price",
    },
    {
      name: "BUSY Accounting",
      code: "BUSY-ACC",
      price: "Contact for Price",
    },
    {
      name: "Odoo ERP",
      code: "ODOO-ERP",
      price: "Contact for Price",
    },
    {
      name: "Miracle",
      code: "MIRACLE",
      price: "Contact for Price",
    },
    {
      name: "Ottu HRMS",
      code: "OTTU-HRMS",
      price: "Contact for Price",
    },
    {
      name: "Custom Software",
      code: "CUSTOM-SOFTWARE",
      price: "Contact for Price",
    },
  ];

  /* =====================================================
     FETCH CONTACT DETAILS
  ===================================================== */

  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        setContactLoading(true);

        const response = await fetch(`${BASE_URL}/contact.php`);

        const result = await response.json();

        console.log("CONTACT RESPONSE:", result);

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Failed to fetch contact details");
        }

        const data = result.data;

        setContactData({
          phone: data.phone || "",
          email: data.email || "",
          address: data.address || "",
          businessHours: data.business_hours || "",
        });
      } catch (error) {
        console.error("FETCH CONTACT ERROR:", error);
      } finally {
        setContactLoading(false);
      }
    };

    fetchContactDetails();
  }, []);

  /* =====================================================
     URL PARAMETER / MODULE DATA
  ===================================================== */

  useEffect(() => {
    const productName = searchParams.get("product") || "";
    const moduleName = searchParams.get("module") || "";
    const title = searchParams.get("title") || "";
    const moduleCode = searchParams.get("code") || "";
    const modulePrice = searchParams.get("price") || "";
    const planName = searchParams.get("plan") || "";
    const automaticMessage = searchParams.get("message") || "";

    let selectedModule = title;

    if (!selectedModule) {
      const parts = [];

      if (productName) parts.push(productName);
      if (moduleName) parts.push(moduleName);
      if (planName) parts.push(planName);

      selectedModule = parts.join(" - ");
    }

    setFormData((prev) => ({
      ...prev,
      module: selectedModule,
      moduleCode: moduleCode,
      modulePrice: modulePrice,
      message: automaticMessage,
    }));
  }, [searchParams]);

  /* =====================================================
     HANDLE CHANGE
  ===================================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "module") {
      const selectedModule = modules.find((item) => item.name === value);

      setFormData((prev) => ({
        ...prev,
        module: value,
        moduleCode: selectedModule?.code || "",
        modulePrice: selectedModule?.price || "",
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =====================================================
     SUBMIT ENQUIRY
  ===================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BASE_URL}/inquiries.php?action=add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      console.log("INQUIRY RESPONSE:", result);

      if (!result.status) {
        alert(result.message || "Failed to submit enquiry");

        return;
      }

      alert(
        "Thank you! Your enquiry has been submitted successfully. Our team will contact you shortly.",
      );

      setFormData({
        fullName: "",
        email: "",
        phone: "",
        businessName: "",
        module: "",
        moduleCode: "",
        modulePrice: "",
        message: "",
      });
    } catch (error) {
      console.error("Submit enquiry error:", error);

      alert("Unable to submit your enquiry. Please try again later.");
    }
  };

  /* =====================================================
     WHATSAPP NUMBER
  ===================================================== */

  const getWhatsAppNumber = () => {
    if (!contactData.phone) {
      return "";
    }

    // Remove +, spaces, brackets, hyphens etc.
    return contactData.phone.replace(/\D/g, "");
  };

  const whatsappNumber = getWhatsAppNumber();

  const whatsappMessage = encodeURIComponent(
    "Hello Star Computers, I need immediate assistance regarding your software solutions.",
  );

  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`
    : "#";

  /* =====================================================
     LOADING
  ===================================================== */

  if (contactLoading) {
    return (
      <main className="contact-page">
        <section className="contact-section">
          <div className="section-container">
            <div className="contact-loading">Loading contact details...</div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="contact-page">
      {/* =====================================================
          PAGE HERO
      ===================================================== */}

      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>

        <div className="section-container contact-hero-content">
          <span className="section-label">GET IN TOUCH</span>

          <h1>
            Let's Talk About
            <span> Your Business</span>
          </h1>

          <p>
            Have a question about our software solutions? Contact Star Computers
            and let our experts help you find the right solution for your
            business.
          </p>
        </div>
      </section>

      {/* =====================================================
          CONTACT SECTION
      ===================================================== */}

      <section className="contact-section">
        <div className="section-container">
          <div className="contact-grid">
            {/* =================================================
                LEFT INFORMATION
            ================================================= */}

            <div className="contact-info">
              <span className="section-label">CONTACT STAR COMPUTERS</span>

              <h2>
                We're Here to
                <span> Help Your Business</span>
              </h2>

              <p className="contact-info-description">
                Whether you need accounting software, ERP, HRMS or a customized
                business solution, our experienced team is ready to assist you.
              </p>

              <div className="contact-info-list">
                {/* PHONE */}

                <div className="contact-info-card">
                  <div className="contact-info-icon">
                    <FaPhoneAlt />
                  </div>

                  <div>
                    <span>Call Us</span>

                    <strong>{contactData.phone}</strong>
                  </div>
                </div>

                {/* EMAIL */}

                <div className="contact-info-card">
                  <div className="contact-info-icon">
                    <FaEnvelope />
                  </div>

                  <div>
                    <span>Email Us</span>

                    <strong>{contactData.email}</strong>
                  </div>
                </div>

                {/* ADDRESS */}

                <div className="contact-info-card">
                  <div className="contact-info-icon">
                    <FaMapMarkerAlt />
                  </div>

                  <div>
                    <span>Visit Us</span>

                    <strong>{contactData.address}</strong>
                  </div>
                </div>

                {/* BUSINESS HOURS */}

                <div className="contact-info-card">
                  <div className="contact-info-icon">
                    <FaClock />
                  </div>

                  <div>
                    <span>Business Hours</span>

                    <strong>{contactData.businessHours}</strong>
                  </div>
                </div>
              </div>

              {/* =================================================
                  WHATSAPP BOX
              ================================================= */}

              <div className="contact-whatsapp-box">
                <div className="contact-whatsapp-icon">
                  <FaWhatsapp />
                </div>

                <div className="contact-whatsapp-content">
                  <span>CHAT WITH US</span>

                  <strong>WhatsApp Support</strong>

                  <p>Get quick assistance from our software experts.</p>
                </div>

                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-whatsapp-btn"
                  onClick={(e) => {
                    if (!whatsappNumber) {
                      e.preventDefault();

                      alert("WhatsApp number is currently unavailable.");
                    }
                  }}
                >
                  <FaWhatsapp />
                  WhatsApp
                </a>
              </div>
            </div>

            {/* =================================================
                CONTACT FORM
            ================================================= */}

            <div className="contact-form-wrapper">
              <div className="contact-form-header">
                <span>ENQUIRY FORM</span>

                <h2>Send Us a Message</h2>

                <p>
                  Fill in the details below and our team will get back to you
                  shortly.
                </p>
              </div>

              <form className="contact-enquiry-form" onSubmit={handleSubmit}>
                {/* FULL NAME */}

                <div className="contact-form-field">
                  <label className="contact-form-label">
                    Full Name
                    <span>*</span>
                  </label>

                  <div className="contact-form-input">
                    <FaUser />

                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </div>

                {/* EMAIL */}

                <div className="contact-form-field">
                  <label className="contact-form-label">Email Address</label>

                  <div className="contact-form-input">
                    <FaEnvelope />

                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                {/* PHONE */}

                <div className="contact-form-field">
                  <label className="contact-form-label">
                    Phone Number
                    <span>*</span>
                  </label>

                  <div className="contact-form-input">
                    <FaPhoneAlt />

                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>
                </div>

                {/* BUSINESS NAME */}

                <div className="contact-form-field">
                  <label className="contact-form-label">Business Name</label>

                  <div className="contact-form-input">
                    <FaBuilding />

                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      placeholder="Enter your business name"
                    />
                  </div>
                </div>

                {/* SELECTED MODULE */}

                <div className="contact-form-field">
                  <label className="contact-form-label">Selected Module</label>

                  <div className="contact-form-input">
                    <FaCube />

                    <input
                      type="text"
                      name="module"
                      value={formData.module}
                      readOnly
                      placeholder="Selected module"
                    />
                  </div>
                </div>

                {/* MODULE CODE */}

                <div className="contact-form-field">
                  <label className="contact-form-label">Module Code</label>

                  <div className="contact-form-input">
                    <FaTag />

                    <input
                      type="text"
                      name="moduleCode"
                      value={formData.moduleCode}
                      readOnly
                      placeholder="Module code"
                    />
                  </div>
                </div>

                {/* MODULE PRICE */}

                <div className="contact-form-field">
                  <label className="contact-form-label">Module Price</label>

                  <div className="contact-form-input">
                    <FaRupeeSign />

                    <input
                      type="text"
                      name="modulePrice"
                      value={formData.modulePrice}
                      readOnly
                      placeholder="Module price"
                    />
                  </div>
                </div>

                {/* MESSAGE */}

                <div className="contact-form-field contact-form-field-full">
                  <label className="contact-form-label">Message</label>

                  <div className="contact-form-textarea">
                    <FaCommentAlt />

                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your requirements..."
                      rows="5"
                    />
                  </div>
                </div>

                {/* SUBMIT */}

                <div className="contact-form-submit contact-form-field-full">
                  <button type="submit" className="contact-form-submit-btn">
                    Send Enquiry
                    <FaPaperPlane />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
